import { useEffect, useState, useCallback, Fragment } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card, CardContent } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Calendar as CalendarIcon,
  FileDown,
  Search,
  ChevronDown,
  ChevronRight,
  ChefHat,
  TrendingUp,
  TrendingDown,
  Percent,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  get_recipe_costing_report,
  get_report_filter_data,
  server_post_data,
} from "../ServiceConnection/serviceconnection";
import XLSX from "sheetjs-style";
import { handleError } from "../CommonJquery/CommonJquery";

const varianceBadgeClass = (variance) => {
  if (variance > 0) return "bg-destructive/10 text-destructive";
  if (variance < 0) return "bg-success/10 text-success";
  return "bg-muted text-muted-foreground";
};

export default function RecipeCostingReport() {
  const { toast } = useToast();

  const [filterOption, setFilterOption] = useState("");
  const [startCal, setStartCal] = useState(new Date());
  const [endCal, setEndCal] = useState(new Date());
  const [menuItemId, setMenuItemId] = useState("all");

  const [menuItems, setMenuItems] = useState([]);

  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [recipeData, setRecipeData] = useState([]);
  const [expandedRecipeId, setExpandedRecipeId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;

  const [stats, setStats] = useState({
    totalRecipes: 0,
    avgTheoreticalCost: 0,
    avgActualCost: 0,
    avgVariance: 0,
  });

  const formatNumber = (num) => {
    if (num === "-") return "-";
    if (num === undefined || num === null) return 0;
    const parsed = parseFloat(num);
    if (isNaN(parsed)) return 0;
    return parsed % 1 === 0 ? parsed : parseFloat(parsed.toFixed(2));
  };

  const formatDateLocal = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const fetchFilterData = useCallback(async () => {
    try {
      const fd = new FormData();
      const response = await server_post_data(get_report_filter_data, fd);
      if (!response.data.error) {
        setMenuItems(response.data.message.menu_items || []);
      }
    } catch (error) {
      console.error("Error fetching filter data:", error);
    }
  }, []);

  const fetchRecipeData = useCallback(
    async (start_date, end_date, flag = "1") => {
      if (flag === "2") {
        if (filterOption === "today") {
          const today = new Date();
          start_date = new Date(today.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_week") {
          const today = new Date();
          const firstDayOfWeek = new Date(
            today.setDate(today.getDate() - today.getDay()),
          );
          start_date = new Date(firstDayOfWeek.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_month") {
          const today = new Date();
          start_date = new Date(
            today.getFullYear(),
            today.getMonth(),
            1,
            0,
            0,
            0,
          );
          end_date = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0,
            23,
            59,
            59,
          );
        } else if (filterOption === "this_year") {
          const today = new Date();
          start_date = new Date(today.getFullYear(), 0, 1, 0, 0, 0);
          end_date = new Date(today.getFullYear(), 11, 31, 23, 59, 59);
        } else if (filterOption === "custom") {
          if (!startCal || !endCal) {
            toast({
              title: "Validation Error",
              description: "Please select both start and end dates.",
              variant: "destructive",
            });
            return;
          }
          start_date = startCal;
          end_date = endCal;
        }

        if (start_date > end_date) {
          toast({
            title: "Validation Error",
            description: "Start date cannot be greater than end date.",
            variant: "destructive",
          });
          return;
        }
      }

      setShowLoaderAdmin(true);
      const fd = new FormData();
      fd.append("start_date", formatDateLocal(start_date));
      fd.append("end_date", formatDateLocal(end_date));
      if (menuItemId !== "all") fd.append("menu_item_id", menuItemId);

      try {
        const response = await server_post_data(get_recipe_costing_report, fd);
        if (response.data.error) {
          toast({
            title: "Error",
            description: response.data.message,
            variant: "destructive",
          });
        } else {
          // Backend returns { error, data: [...], summary: {...} } directly
          const payload = response.data || {};
          const summary = payload.summary || {};
          const num = (v) => Number(v) || 0;

          // Naye backend ki keys purane naam pe — table / expand / export same
          const data = (payload.data || []).map((r) => {
            const actualPerUnit = num(
              r.actual_cost_per_unit ?? r.actual_per_unit,
            );
            const deductedQty = num(r.units_with_stock_deducted);

            const baseName = r.menu_item_name || r.recipe_name || "-";
            const name = r.variant ? `${baseName} (${r.variant})` : baseName;

            return {
              ...r,
              menu_item_name: name,
              theoretical_food_cost_percentage:
                r.food_cost_pct ?? r.theoretical_food_cost_percentage ?? 0,
              actual_per_unit: actualPerUnit,
              actual_cost: r.actual_cost ?? actualPerUnit * deductedQty,
              sales_qty: r.units_sold ?? r.sales_qty ?? 0,
              cost_variance: r.variance ?? r.cost_variance ?? 0,
              variance_percentage: r.variance_pct ?? r.variance_percentage ?? 0,
              actual_food_cost_percentage:
                r.actual_food_cost_pct ?? r.actual_food_cost_percentage ?? 0,
              ingredients: (r.ingredients || []).map((ing) => ({
                ...ing,
                // rate per entered unit (per KG / per Spoon) — cost ÷ qty
                avg_rate:
                  ing.avg_rate ??
                  (num(ing.quantity) > 0
                    ? num(ing.cost) / num(ing.quantity)
                    : 0),
                ingredient_name: ing.is_sub_recipe
                  ? `${ing.ingredient_name} (sub-recipe)`
                  : ing.ingredient_name,
              })),
            };
          });

          // Averages yahin nikalo — actual sirf un recipes ka jinka stock kata
          const withActual = data.filter((r) => num(r.actual_per_unit) > 0);
          const avg = (arr, key) =>
            arr.length
              ? arr.reduce((s, r) => s + num(r[key]), 0) / arr.length
              : 0;

          setRecipeData(data);
          setExpandedRecipeId(null);
          setCurrentPage(1);
          setStats({
            totalRecipes: summary.total_recipes ?? data.length,
            avgTheoreticalCost:
              summary.avg_theoretical_cost ?? avg(data, "theoretical_cost"),
            avgActualCost:
              summary.avg_actual_cost ?? avg(withActual, "actual_per_unit"),
            avgVariance:
              summary.avg_variance ?? avg(withActual, "cost_variance"),
          });
        }
      } catch (error) {
        console.error("Error fetching recipe costing data:", error);
        toast({
          title: "Error",
          description: "Failed to load recipe costing data",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [filterOption, startCal, endCal, menuItemId, toast],
  );

  const handleSearch = useCallback(() => {
    fetchRecipeData(startCal, endCal, "2");
  }, [startCal, endCal, fetchRecipeData]);

  function downloadExcel(rows, filename = "Recipe_Costing_Report.xlsx") {
    if (!rows.length) {
      handleError("No data available for export!");
      return;
    }

    const summarySheetData = rows.map((item, index) => ({
      "Sr. No.": index + 1,
      "Menu Item": item.menu_item_name || "-",
      Category: item.category || "-",
      "Selling Price (₹)": formatNumber(item.selling_price),
      "Theoretical Cost (₹)": formatNumber(item.theoretical_cost),
      "Theoretical Cost %": formatNumber(item.theoretical_food_cost_percentage),
      "Actual Cost (₹)": formatNumber(item.actual_cost),
      "Actual Per Unit (₹)": formatNumber(item.actual_per_unit),
      "Sales Qty": formatNumber(item.sales_qty),
      "Cost Variance (₹)": formatNumber(item.cost_variance),
      "Variance %": formatNumber(item.variance_percentage),
      "Actual Food Cost %": formatNumber(item.actual_food_cost_percentage),
    }));

    summarySheetData.push({
      "Sr. No.": "",
      "Menu Item": "TOTAL / AVG",
      Category: "",
      "Selling Price (₹)": "",
      "Theoretical Cost (₹)": formatNumber(stats.avgTheoreticalCost),
      "Theoretical Cost %": "",
      "Actual Cost (₹)": "",
      "Actual Per Unit (₹)": formatNumber(stats.avgActualCost),
      "Sales Qty": "",
      "Cost Variance (₹)": formatNumber(stats.avgVariance),
      "Variance %": "",
      "Actual Food Cost %": `Recipes: ${stats.totalRecipes}`,
    });

    const ingredientSheetData = [];
    rows.forEach((recipe) => {
      (recipe.ingredients || []).forEach((ing) => {
        ingredientSheetData.push({
          "Menu Item": recipe.menu_item_name || "-",
          Ingredient: ing.ingredient_name || "-",
          "Recipe Qty": formatNumber(ing.quantity),
          Unit: ing.unit || "-",
          "Rate (₹)": formatNumber(ing.avg_rate),
          "Cost (₹)": formatNumber(ing.cost),
        });
      });
    });

    const summarySheet = XLSX.utils.json_to_sheet(summarySheetData);
    summarySheet["!cols"] = [
      { wch: 8 },
      { wch: 22 },
      { wch: 16 },
      { wch: 14 },
      { wch: 16 },
      { wch: 14 },
      { wch: 14 },
      { wch: 16 },
      { wch: 11 },
      { wch: 14 },
      { wch: 11 },
      { wch: 14 },
    ];

    const ingredientSheet = XLSX.utils.json_to_sheet(ingredientSheetData);
    ingredientSheet["!cols"] = [
      { wch: 22 },
      { wch: 22 },
      { wch: 11 },
      { wch: 8 },
      { wch: 10 },
      { wch: 10 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Recipe Summary");
    XLSX.utils.book_append_sheet(
      workbook,
      ingredientSheet,
      "Ingredient Detail",
    );
    XLSX.writeFile(workbook, filename);
  }

  const totalPages = Math.ceil(recipeData.length / entriesPerPage);
  const currentData = recipeData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage,
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
    setExpandedRecipeId(null);
  };

  const toggleExpand = (recipeId) => {
    setExpandedRecipeId(expandedRecipeId === recipeId ? null : recipeId);
  };

  useEffect(() => {
    fetchFilterData();
    fetchRecipeData(startCal, endCal, "1");
  }, []);

  return (
    <MainLayout>
      <Header
        title="Recipe Costing Report"
        subtitle="Theoretical vs actual cost analysis by recipe"
      />

      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <ChefHat className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalRecipes}
                </p>
                <p className="text-sm text-muted-foreground">Total Recipes</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <Percent className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  ₹{formatNumber(stats.avgTheoreticalCost)}
                </p>
                <p className="text-sm text-muted-foreground">
                  Avg Theoretical Cost
                </p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                <Percent className="w-5 h-5 text-accent" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  ₹{formatNumber(stats.avgActualCost)}
                </p>
                <p className="text-sm text-muted-foreground">Avg Actual Cost</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                {stats.avgVariance > 0 ? (
                  <TrendingUp className="w-5 h-5 text-destructive" />
                ) : (
                  <TrendingDown className="w-5 h-5 text-success" />
                )}
              </div>
              <div>
                <p
                  className={cn(
                    "text-2xl font-bold",
                    stats.avgVariance > 0
                      ? "text-destructive"
                      : stats.avgVariance < 0
                        ? "text-success"
                        : "text-foreground",
                  )}
                >
                  {stats.avgVariance > 0 ? "+" : ""}₹
                  {formatNumber(stats.avgVariance)}
                </p>
                <p className="text-sm text-muted-foreground">Avg Variance</p>
              </div>
            </div>
          </div>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Filter</Label>
                <Select value={filterOption} onValueChange={setFilterOption}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="this_week">This Week</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                    <SelectItem value="this_year">This Year</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !startCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startCal ? (
                          format(startCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startCal}
                        onSelect={setStartCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !endCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endCal ? (
                          format(endCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endCal}
                        onSelect={setEndCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              <div className="space-y-2">
                <Label>Menu Item</Label>
                <Select value={menuItemId} onValueChange={setMenuItemId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Menu Items" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Menu Items</SelectItem>
                    {menuItems.map((mi) => (
                      <SelectItem
                        key={mi.primary_id}
                        value={String(mi.primary_id)}
                      >
                        {mi.menu_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end gap-2">
                <Button
                  onClick={handleSearch}
                  className="gap-2"
                  disabled={showLoaderAdmin}
                >
                  <Search className="w-4 h-4" /> Search
                </Button>
                <Button
                  variant="outline"
                  onClick={() => downloadExcel(recipeData)}
                  className="gap-2"
                  disabled={recipeData.length === 0}
                >
                  <FileDown className="w-4 h-4" /> Export Excel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground w-10"></TableHead>
                  <TableHead className="text-muted-foreground">Sr.No</TableHead>
                  <TableHead className="text-muted-foreground">
                    Menu Item
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Category
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Selling Price
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Theoretical Cost
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Actual /Unit
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Sales Qty
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Variance
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Food Cost %
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {showLoaderAdmin ? (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      className="text-center text-muted-foreground"
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : currentData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      className="text-center text-muted-foreground"
                    >
                      No data found
                    </TableCell>
                  </TableRow>
                ) : (
                  currentData.map((row, index) => (
                    <Fragment key={row.recipe_id || index}>
                      <TableRow
                        className="border-border cursor-pointer"
                        onClick={() => toggleExpand(row.recipe_id)}
                      >
                        <TableCell>
                          {expandedRecipeId === row.recipe_id ? (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell>
                          {(currentPage - 1) * entriesPerPage + index + 1}
                        </TableCell>
                        <TableCell className="font-medium">
                          {row.menu_item_name || "-"}
                        </TableCell>
                        <TableCell>{row.category || "-"}</TableCell>
                        <TableCell className="text-right">
                          {formatNumber(row.selling_price)}
                        </TableCell>
                        <TableCell className="text-right">
                          ₹{formatNumber(row.theoretical_cost)}
                          <span className="text-xs text-muted-foreground ml-1">
                            (
                            {formatNumber(row.theoretical_food_cost_percentage)}
                            %)
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          ₹{formatNumber(row.actual_per_unit)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(row.sales_qty)}
                        </TableCell>
                        <TableCell className="text-right">
                          <span
                            className={cn(
                              "inline-flex items-center px-2 py-1 rounded-md text-xs font-medium",
                              varianceBadgeClass(row.cost_variance),
                            )}
                          >
                            {row.cost_variance > 0 ? "+" : ""}₹
                            {formatNumber(row.cost_variance)} (
                            {formatNumber(row.variance_percentage)}%)
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(row.actual_food_cost_percentage)}%
                        </TableCell>
                      </TableRow>

                      {expandedRecipeId === row.recipe_id && (
                        <TableRow className="border-border bg-muted/30 hover:bg-muted/30">
                          <TableCell colSpan={10} className="p-4">
                            <div className="rounded-lg border border-border bg-card overflow-hidden">
                              <Table>
                                <TableHeader>
                                  <TableRow className="border-border hover:bg-transparent">
                                    <TableHead className="text-muted-foreground text-xs">
                                      Ingredient
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs text-right">
                                      Recipe Qty
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs">
                                      Unit
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs text-right">
                                      Rate (₹)
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs text-right">
                                      Cost (₹)
                                    </TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {(row.ingredients || []).map((ing, i) => (
                                    <TableRow
                                      key={`${row.recipe_id}-${ing.ingredient_id}-${i}`}
                                      className="border-border"
                                    >
                                      <TableCell className="font-medium text-sm">
                                        {ing.ingredient_name || "-"}
                                      </TableCell>
                                      <TableCell className="text-right text-sm">
                                        {formatNumber(ing.quantity)}
                                      </TableCell>
                                      <TableCell className="text-sm">
                                        {ing.unit || "-"}
                                      </TableCell>
                                      <TableCell className="text-right text-sm">
                                        {formatNumber(ing.avg_rate)}
                                      </TableCell>
                                      <TableCell className="text-right text-sm font-semibold">
                                        {formatNumber(ing.cost)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * entriesPerPage + 1} to{" "}
                {Math.min(currentPage * entriesPerPage, recipeData.length)} of{" "}
                {recipeData.length} entries
              </div>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 &&
                      pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <Button
                        key={pageNumber}
                        variant={
                          pageNumber === currentPage ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Button>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return <span key={pageNumber}>...</span>;
                  }
                  return null;
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
